import type { OwnListMemberName } from "../data/own-list-keys.js";

export type { OwnListMemberName } from "../data/own-list-keys.js";

/**
 * A List 词单 a reader makes for themselves, its parts and its members.
 *
 * #869. A published List is content the site writes once for everybody and a
 * reader follows. An own List is the same shape held by one reader: a name,
 * members in an order, and parts, each part a List of its own under it. The
 * row shapes are in `./own-list-row.ts` and the keys in
 * `src/data/own-list-keys.ts`. docs/lists.md under "A reader's own Lists"
 * has the design.
 *
 * **One level of parts, and members at both levels.** A published List nests
 * as deep as a band of the syllabus needs. A reader cutting a textbook into
 * chapters needs a List and its parts, and every level past that is a control
 * to learn for a structure almost nobody wants.
 *
 * **Each List and part has a path, which is how the rest of the site names
 * it.** `<list>` for a List and `<list>/<part>` for a part. The set of Lists
 * on a saved word holds paths, and following one in 复习 will name it under
 * `/cidan/` as `mine/<path>`.
 */

/** A List of the reader's own, at the top. */
export interface OwnList {
  readonly id: string;
  readonly name: string;

  /** How many members the List holds itself, its parts' aside. */
  readonly members: number;

  /** How many members it holds with its parts', which the cap is counted on. */
  readonly total: number;

  /** How many parts it has. */
  readonly parts: number;

  /**
   * Which edition this is, moved by every change to what the List holds or
   * the order it holds it in.
   *
   * What a published List's `version` is to its walk. Renaming leaves it.
   */
  readonly version: number;

  readonly createdAt: Date;

  /** When anything about it last changed, which is what the Lists are ordered by. */
  readonly updatedAt: Date;
}

/** A part of a List. */
export interface OwnListPart {
  /** The List it is a part of. */
  readonly list: string;
  readonly id: string;
  readonly name: string;

  /** Where it sits among the List's parts. See `./own-list-rank.ts`. */
  readonly rank: string;

  readonly members: number;
  readonly createdAt: Date;
}

/** A member of a List or a part. */
export interface OwnListMember {
  readonly list: string;

  /** The part it is in, or the List's own id for the List's own members. */
  readonly part: string;

  readonly member: OwnListMemberName;

  /** Where it sits among the members beside it. See `./own-list-rank.ts`. */
  readonly rank: string;

  readonly addedAt: Date;
}

/** A List with its parts, in their order. */
export interface OwnListWhole {
  readonly list: OwnList;
  readonly parts: readonly OwnListPart[];
}

/** How many Lists of their own a free account keeps, the line #1076 drew for following. */
export const freeOwnLists = 1;

/** How many a Pro member keeps. */
export const proOwnLists = 100;

/** How many parts one List has at most. */
export const ownListParts = 30;

/**
 * How many members one List holds at most, its parts' included.
 *
 * A whole textbook's vocabulary. The bound is what keeps reading a List, and
 * deleting one, a request rather than a job.
 */
export const ownListMembers = 2000;

/** How long a name may be, in characters. */
export const longestOwnListName = 60;

/**
 * How many Lists a reader may hold.
 * @param pro - Whether they hold Pro.
 * @returns The cap.
 */
export function ownListCapOf(pro: boolean): number {
  return pro ? proOwnLists : freeOwnLists;
}

/** The letters an id is drawn from: lowercase, and none that read as another. */
const idLetters = "abcdefghijkmnpqrstuvwxyz23456789";

/** How long an id is. Fixed, so no List's key begins another's. */
export const ownListIdLength = 10;

/** The shape of an id: that many of those letters and nothing else. */
const idPattern = new RegExp(
  `^[${idLetters}]{${String(ownListIdLength)}}$`,
  "u",
);

/**
 * A new id for a List or a part.
 *
 * Ten letters from thirty-two, so fifty bits, drawn at random. Web Crypto
 * rather than `node:crypto`, since the markup that names a List reaches the
 * browser bundles through the copy beside it. An id names
 * one List under one reader, so it has to differ only from that reader's
 * others. Lowercase letters and digits are the shape a List's path takes,
 * which is what lets a List be named under `/cidan/` for following.
 * @returns The id.
 */
export function newOwnListId(): string {
  return Array.from(
    crypto.getRandomValues(new Uint8Array(ownListIdLength)),
    (byte) => idLetters[byte % idLetters.length],
  ).join("");
}

/**
 * Whether a string could be an id, checked before a key is built from it.
 * @param id - What arrived.
 * @returns Whether it has the shape.
 */
export function isOwnListId(id: string): boolean {
  return idPattern.test(id);
}

/** What cuts a name into what a reader sees as characters. */
const graphemes = new Intl.Segmenter("en", { granularity: "grapheme" });

/**
 * A name as typed, made into one the List can carry, or nothing.
 *
 * Runs of space become one and the ends are trimmed. A name too long is cut
 * rather than refused, since the field says how long it may be and a browser
 * holds a reader to it already.
 * @param typed - What the reader typed.
 * @returns The name, or nothing for one with nothing in it.
 */
export function ownListName(typed: string): string | undefined {
  const name = Array.from(
    graphemes.segment(typed.replaceAll(/\s+/gu, " ").trim()),
    (each) => each.segment,
  )
    .slice(0, longestOwnListName)
    .join("")
    .trim();

  return name === "" ? undefined : name;
}

/**
 * The path of a List or a part, as the set on a saved word holds it.
 * @param list - The List's id.
 * @param part - The part's id, or the List's own for the List itself.
 * @returns The path.
 */
export function ownListPath(list: string, part: string): string {
  return part === list ? list : `${list}/${part}`;
}

/**
 * The List and part a path names, or nothing for a path of another shape.
 * @param path - The path.
 * @returns The ids, the part being the List's own for the List itself.
 */
export function ownListOfPath(
  path: string,
): { list: string; part: string } | undefined {
  const [list, part, ...rest] = path.split("/");

  if (list === undefined || !isOwnListId(list) || rest.length > 0) {
    return undefined;
  }

  if (part === undefined) {
    return { list, part: list };
  }

  return isOwnListId(part) ? { list, part } : undefined;
}
